import { MUTATIONS, useMutation } from "../../../api";
import type { WorkshopId } from "../../../api/workshop";
import Button from "../../../design-system/components/Button/Button.jsx";
import { Variants } from "../../../design-system/components/Button/Variants.jsx";
import Dialog from "../../../design-system/components/Dialog/Dialog.jsx";
import Text from "../../../design-system/primitives/components/Text/Text";
import Toast, {
  ToastProvider,
} from "../../../design-system/primitives/components/Toast/Toast";
import { Roles } from "../../../lib/Roles.jsx";
import { useSparkContext } from "../context/SparkContext.jsx";
import styles from "./classroomManagement.css";
import { useLiveState } from "./hooks/useLiveState";
import { dismissMessage } from "./liveStore";

/**
 * Always-mounted display layer for a workshop's live classroom-management state
 * (the pause screen and the broadcast message).
 *
 * Rendered by the SparkShell (not the toolbar) so it reaches teachers and
 * students on any route, including while they're inside a step.
 *
 * Teachers see a toast while a pause is live, with "Aufheben" to lift it — and
 * since the toast's open state is bound to the live pause, dismissing it any way
 * (button or swipe) ends the pause too. Students instead get a blocking pause
 * screen they can't dismiss — they just wait it out.
 *
 * The message is fire-and-forget: the teacher sends it and gets a confirmation
 * (see ClassroomManagementButton), with nothing to manage or end afterwards, so
 * only students see a dialog here. Each student's copy is latched client-side and
 * stays until they tap "Verstanden" (`dismissMessage`), regardless of the
 * server's short replay window.
 */
export const ClassroomManagementOverlay = () => {
  const { workshop } = useSparkContext();
  const { pause, message } = useLiveState(workshop.id);
  const isAdult = Boolean(
    workshop.roles?.includes(Roles.TEACHER) ||
    workshop.roles?.includes(Roles.ADMIN),
  );
  const paused = Boolean(pause);

  if (isAdult) {
    return <AdultPauseToast workshopId={workshop.id} paused={paused} />;
  }

  // Don't stack the message modal on top of the pause screen; the pause is the
  // hard stop and wins until it's lifted.
  const messageShown = Boolean(message) && !paused;
  const content = message?.content ?? "";

  return (
    <>
      <StudentPauseDialog paused={paused} />
      <StudentMessageDialog
        open={messageShown}
        content={content}
        onDismiss={() => dismissMessage(workshop.id)}
      />
    </>
  );
};

const AdultPauseToast = ({
  workshopId,
  paused,
}: {
  workshopId: WorkshopId;
  paused: boolean;
}) => {
  const [manageWorkshop] = useMutation(MUTATIONS.manageWorkshop);

  // The toast's open state is bound to the live pause, so closing it (via the
  // "Aufheben" button or a swipe) ends the pause; the cleared state then comes
  // back over SSE and drops `paused`, closing the toast.
  const endPause = () => {
    void manageWorkshop({ workshopId, action: { action: "endPause" } });
  };

  return (
    <ToastProvider>
      <Toast
        open={paused}
        onOpenChange={(open) => {
          if (!open) endPause();
        }}
        icon="lock"
        title="Pause aktiv"
        description="Alle Schüler*innen-Bildschirme sind gesperrt."
        closeLabel="Aufheben"
        duration={Infinity}
      />
    </ToastProvider>
  );
};

const StudentPauseDialog = ({ paused }: { paused: boolean }) => (
  <Dialog
    open={paused}
    setOpen={() => {}}
    withButtonClose={false}
    title="Kurze Pause"
    buttons={[]}
  >
    <Text variant="body" color="neutral-100">
      Deine Lehrkraft hat den Bildschirm pausiert. Schau nach vorne — gleich
      geht’s weiter.
    </Text>
  </Dialog>
);

const StudentMessageDialog = ({
  open,
  content,
  onDismiss,
}: {
  open: boolean;
  content: string;
  onDismiss: () => void;
}) => (
  <Dialog
    open={open}
    setOpen={onDismiss}
    title="Nachricht von deiner Lehrkraft"
    buttons={[
      <Button variant={Variants.PRIMARY} onClick={onDismiss}>
        Verstanden
      </Button>,
    ]}
  >
    <div className={styles.messageBox}>{content}</div>
  </Dialog>
);
